import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import type { McpToken } from 'api';
import { Restricted } from 'containers/Restricted';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import {
  MCP_CREATE_BUTTON_LABEL,
  MCP_ERROR_MESSAGE,
  MCP_ERROR_TEST_ID,
  MCP_HEADER_CREATE_TEST_ID,
  MCP_LOADING_TEST_ID,
  MCP_PAGE_TEST_ID,
  MCP_PAGE_TITLE,
  MCP_TITLE_TEST_ID
} from './constants';
import { CreateMcpTokenModal } from './CreateMcpTokenModal';
import { McpEmptyState } from './McpEmptyState';
import { McpTokenGrid } from './McpTokenGrid';
import { RevokeMcpTokenModal } from './RevokeMcpTokenModal';

import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { DevelopersPageHeader } from '../DevelopersPageHeader';
import { useMcpTokens } from '../hooks/useMcpTokens';

export const Mcp = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<McpToken | null>(null);
  const { tokens, isLoading, isError } = useMcpTokens();

  const openCreate = () => setIsCreateOpen(true);
  const closeCreate = () => setIsCreateOpen(false);

  const showEmptyState = !isLoading && !isError && tokens.length === 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          data-testid={MCP_LOADING_TEST_ID}
          className={`flex ${EMPTY_STATE_MIN_HEIGHT} items-center justify-center`}
        >
          <Spinner size="large" />
        </div>
      );
    }

    if (isError) {
      return (
        <div
          data-testid={MCP_ERROR_TEST_ID}
          className="flex items-center justify-center py-12 text-content-secondary"
        >
          {MCP_ERROR_MESSAGE}
        </div>
      );
    }

    if (showEmptyState) {
      return <McpEmptyState onCreate={openCreate} />;
    }

    return (
      <div className="flex flex-col gap-6 pb-6">
        <McpTokenGrid tokens={tokens} onRevoke={setTokenToRevoke} />
      </div>
    );
  };

  return (
    <>
      <TableLayout
        containerTestId={MCP_PAGE_TEST_ID}
        header={
          <DevelopersPageHeader
            title={MCP_PAGE_TITLE}
            titleTestId={MCP_TITLE_TEST_ID}
            action={
              !showEmptyState && (
                <Restricted requiredPermission="developers:manage">
                  <PrimaryButton
                    data-testid={MCP_HEADER_CREATE_TEST_ID}
                    icon={<PlusIcon className="size-4" />}
                    onClick={openCreate}
                  >
                    {MCP_CREATE_BUTTON_LABEL}
                  </PrimaryButton>
                </Restricted>
              )
            }
          />
        }
        content={renderContent()}
      />
      <CreateMcpTokenModal open={isCreateOpen} onClose={closeCreate} />
      {tokenToRevoke && (
        <RevokeMcpTokenModal
          token={tokenToRevoke}
          open
          onCancel={() => setTokenToRevoke(null)}
          onConfirmed={() => setTokenToRevoke(null)}
        />
      )}
    </>
  );
};
